export const COLORWAY = [
    "#2186c5",
    "#43a04c",
    "#f44336",
    "#f8a031",
    "#ba46b8",
    "#009688",
    "#ff5722",
    "#ec407a",
    "#c0ca33",
    "#795548",
    "#e5878a",
    "#9e9e9e"
];

const fallbackColor = "#2186c5";

export function getChartColor(index: number): string {
    try {
        const style = getComputedStyle(document.body);
        const color = style.getPropertyValue("--color" + (index % 12));
        if (!color || color.trim() === "") return COLORWAY[index % COLORWAY.length] || fallbackColor;
        return color.trim();
    } catch {
        return COLORWAY[index % COLORWAY.length] || fallbackColor;
    }
}

export function axisLabel(title: string | { text?: string } | undefined): string | undefined {
    if (!title) return undefined;
    if (typeof title === "string") return title;
    return title.text;
}

export type ChartLegendPosition = "top" | "bottom";

export interface ChartLayout {
    height?: number;
    width?: number;
    showlegend?: boolean;
    /** TanStack Charts color legend placement (default top). */
    legendPosition?: ChartLegendPosition;
    title?: string | { text?: string };
    colorway?: string[];
    autosize?: boolean;
    autoexpand?: boolean;
    font?: { size?: number; family?: string };
    margin?: { t?: number; r?: number; b?: number; l?: number; pad?: number };
    legend?: Record<string, unknown>;
    barmode?: string;
    showgrid?: boolean;
    yaxis?: {
        title?: string | { text?: string };
        tickformat?: string;
        autorange?: string | boolean;
        range?: number[];
        showgrid?: boolean;
    };
    xaxis?: {
        title?: string | { text?: string };
        tickformat?: string;
        autorange?: string | boolean;
        range?: number[];
        showgrid?: boolean;
    };
}

export function layoutHeight(layout: ChartLayout | undefined, fallback: number): number {
    return typeof layout?.height === "number" ? layout.height : fallback;
}

export function layoutWidth(layout: ChartLayout | undefined): number | undefined {
    return typeof layout?.width === "number" ? layout.width : undefined;
}

/** Percent tick formats such as `.1%` and `,.2%` multiply the raw value by 100. */
export function formatterFromTickformat(tickformat: string | undefined): ((value: number) => string) | undefined {
    if (!tickformat) return undefined;
    const decimalsMatch = tickformat.match(/\.(\d)/);
    const decimals = decimalsMatch ? Number(decimalsMatch[1]) : 0;
    const grouped = tickformat.includes(",");
    if (tickformat.includes("%")) {
        return (value: number) => {
            const scaled = value * 100;
            const body = grouped
                ? scaled.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
                : scaled.toFixed(decimals);
            return body + "%";
        };
    }
    if (tickformat.includes("f") || tickformat.includes("s")) {
        return (value: number) => {
            if (grouped) {
                return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
            }
            return value.toFixed(decimals);
        };
    }
    return undefined;
}

export interface SeriesPoint {
    date: Date;
    value: number;
}

export interface NamedSeries {
    name: string;
    color?: string;
    dash?: boolean;
    fill?: boolean;
    points: SeriesPoint[];
}

function toDate(value: unknown): Date {
    if (value instanceof Date) return value;
    return new Date(String(value));
}

/** TimeSeries from common, or an OpenTimeSeries-like object. */
export function seriesFromTimeSeries(ts: any, color?: string): NamedSeries {
    let dates: unknown[] = [];
    let values: number[] = [];
    let name = "";
    if (ts && typeof ts.getTsdfDates === "function" && typeof ts.getTsdfValues === "function") {
        dates = ts.getTsdfDates();
        values = ts.getTsdfValues();
        name = ts.label || ts.name || "";
    } else if (ts) {
        dates = ts.__dates || [];
        values = ts.__values || ts.values || [];
        name = ts.name || "";
    }
    const points: SeriesPoint[] = [];
    for (let i = 0; i < dates.length; i++) {
        const value = values[i];
        if (typeof value !== "number" || !Number.isFinite(value)) continue;
        const date = toDate(dates[i]);
        if (Number.isNaN(date.getTime())) continue;
        points.push({ date, value });
    }
    return { name, color, points };
}
